import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Crown, LogOut, Home, Users, CreditCard, Settings,
  Plus, Trash2, Edit2, DollarSign, UserCheck, TrendingUp, Share2,
  Gift, Clock, XCircle, Copy, CheckCircle, ExternalLink, AlertTriangle, Menu, MousePointerClick
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [bannerStats, setBannerStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({
    name: '',
    price: '',
    description: '',
    features: '',
    paypal_plan_id: '',
    is_popular: false,
    requires_approval: false,
    order: 0
  });
  const [inviteForm, setInviteForm] = useState({
    tier_id: '',
    duration_days: 30,
    max_uses: 1,
    note: ''
  });
  const [invites, setInvites] = useState([]);
  const [copiedToken, setCopiedToken] = useState(null);
  const [paypalStatus, setPaypalStatus] = useState(null);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, subsRes, tiersRes, refRes, invitesRes, paypalRes, pendingRes, bannerRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users?limit=100`),
        axios.get(`${API}/admin/subscriptions?limit=100`),
        axios.get(`${API}/tiers`),
        axios.get(`${API}/referrals/stats`),
        axios.get(`${API}/admin/invites`),
        axios.get(`${API}/admin/paypal-status`),
        axios.get(`${API}/admin/pending-approvals`),
        axios.get(`${API}/admin/banner-stats`)
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users || usersRes.data);
      setSubscriptions(subsRes.data.subscriptions || subsRes.data);
      setTiers(tiersRes.data);
      setReferralStats(refRes.data);
      setInvites(invitesRes.data);
      setPaypalStatus(paypalRes.data);
      setPendingApprovals(pendingRes.data);
      setBannerStats(bannerRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const openTierDialog = (tier = null) => {
    if (tier) {
      setEditingTier(tier);
      setTierForm({
        name: tier.name,
        price: tier.price.toString(),
        description: tier.description,
        features: tier.features.join('\n'),
        paypal_plan_id: tier.paypal_plan_id || '',
        is_popular: tier.is_popular,
        requires_approval: tier.requires_approval || false,
        order: tier.order
      });
    } else {
      setEditingTier(null);
      setTierForm({
        name: '',
        price: '',
        description: '',
        features: '',
        paypal_plan_id: '',
        is_popular: false,
        requires_approval: false,
        order: tiers.length + 1
      });
    }
    setTierDialogOpen(true);
  };

  const handleSaveTier = async () => {
    try {
      const tierData = {
        name: tierForm.name,
        price: parseFloat(tierForm.price),
        description: tierForm.description,
        features: tierForm.features.split('\n').filter(f => f.trim()),
        paypal_plan_id: tierForm.paypal_plan_id || null,
        is_popular: tierForm.is_popular,
        requires_approval: tierForm.requires_approval,
        order: tierForm.order
      };

      if (editingTier) {
        await axios.put(`${API}/tiers/${editingTier.id}`, { ...tierData, id: editingTier.id });
        toast.success('Tier updated');
      } else {
        await axios.post(`${API}/tiers`, tierData);
        toast.success('Tier created');
      }

      setTierDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save tier');
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm('Are you sure you want to delete this tier?')) return;
    
    try {
      await axios.delete(`${API}/tiers/${tierId}`);
      toast.success('Tier deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete tier');
    }
  };

  const handleMakeAdmin = async (userId) => {
    try {
      await axios.post(`${API}/admin/make-admin/${userId}`);
      toast.success('User promoted to admin');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote user');
    }
  };

  const handleSeedTiers = async () => {
    try {
      await axios.post(`${API}/seed`);
      toast.success('Default tiers created');
      fetchData();
    } catch (error) {
      toast.error('Failed to seed tiers');
    }
  };

  const handleCreateInvite = async () => {
    if (!inviteForm.tier_id) {
      toast.error('Please select a tier');
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/create-invite`, inviteForm);
      toast.success(`Invite link created! Token: ${response.data.token}`);
      setGrantDialogOpen(false);
      setInviteForm({ tier_id: '', duration_days: 30, max_uses: 1, note: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    }
  };

  const handleDeactivateInvite = async (token) => {
    if (!window.confirm('Are you sure you want to deactivate this invite?')) return;
    try {
      await axios.delete(`${API}/admin/invites/${token}`);
      toast.success('Invite deactivated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate invite');
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/redeem/${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    toast.success('Invite link copied!');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleApproveSubscription = async (subscriptionId) => {
    try {
      await axios.post(`${API}/admin/approve-subscription/${subscriptionId}`);
      toast.success('Subscription approved!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to reject this subscription? The payment will be cancelled.')) return;
    try {
      await axios.post(`${API}/admin/reject-subscription/${subscriptionId}`);
      toast.success('Subscription rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleRevokeMembership = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to revoke this membership?')) return;
    try {
      await axios.post(`${API}/admin/revoke-membership/${subscriptionId}`);
      toast.success('Membership revoked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke membership');
    }
  };

  const handleExtendMembership = async (subscriptionId) => {
    const days = window.prompt('Extend by how many days?', '30');
    if (!days) return;
    try {
      await axios.post(`${API}/admin/extend-membership/${subscriptionId}?days=${days}`);
      toast.success(`Membership extended by ${days} days`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extend membership');
    }
  };

  const getStatusBadge = (status, isFree = false) => {
    if (isFree && status === 'ACTIVE') {
      return <span className="badge badge-gold"><Gift className="w-3 h-3 mr-1" /> Free</span>;
    }
    switch (status) {
      case 'ACTIVE':
        return <span className="badge badge-green">Active</span>;
      case 'PENDING':
        return <span className="badge badge-gold">Pending</span>;
      case 'PENDING_APPROVAL':
        return <span className="badge badge-gold"><Clock className="w-3 h-3 mr-1" /> Awaiting Approval</span>;
      case 'CANCELLED':
        return <span className="badge badge-red">Cancelled</span>;
      case 'REVOKED':
        return <span className="badge badge-red">Revoked</span>;
      case 'REJECTED':
        return <span className="badge badge-red">Rejected</span>;
      default:
        return <span className="badge badge-gray">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Crown className="w-6 h-6 text-gold-400" />
          <span className="font-heading text-base font-bold text-white">Admin Panel</span>
        </Link>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-white p-2"
          aria-label="Toggle menu"
        >
          {sidebarOpen ? <XCircle className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 border-r border-white/5 flex flex-col bg-[#050505]
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6 border-b border-white/5 hidden lg:block">
          <Link to="/" className="flex items-center gap-3">
            <Crown className="w-8 h-8 text-gold-400" />
            <span className="font-heading text-lg font-bold text-white">House of Royalty</span>
          </Link>
        </div>
        
        <nav className="flex-1 py-6 mt-14 lg:mt-0 overflow-y-auto">
          <Link to="/dashboard" className="sidebar-link" onClick={() => setSidebarOpen(false)}>
            <Home className="w-5 h-5" />
            Dashboard
          </Link>
          <button
            onClick={() => handleTabChange('overview')}
            className={`sidebar-link w-full ${activeTab === 'overview' ? 'active' : ''}`}
          >
            <TrendingUp className="w-5 h-5" />
            Overview
          </button>
          <button
            onClick={() => handleTabChange('users')}
            className={`sidebar-link w-full ${activeTab === 'users' ? 'active' : ''}`}
          >
            <Users className="w-5 h-5" />
            Users
          </button>
          <button
            onClick={() => handleTabChange('subscriptions')}
            className={`sidebar-link w-full ${activeTab === 'subscriptions' ? 'active' : ''}`}
          >
            <CreditCard className="w-5 h-5" />
            Subscriptions
          </button>
          <button
            onClick={() => handleTabChange('approvals')}
            className={`sidebar-link w-full ${activeTab === 'approvals' ? 'active' : ''}`}
          >
            <UserCheck className="w-5 h-5" />
            Approvals
            {pendingApprovals.length > 0 && (
              <span className="ml-auto bg-amber-500 text-black text-xs px-2 py-0.5 rounded-full">
                {pendingApprovals.length}
              </span>
            )}
          </button>
          <button
            onClick={() => handleTabChange('tiers')}
            className={`sidebar-link w-full ${activeTab === 'tiers' ? 'active' : ''}`}
          >
            <Settings className="w-5 h-5" />
            Tiers
          </button>
          <button
            onClick={() => handleTabChange('referrals')}
            className={`sidebar-link w-full ${activeTab === 'referrals' ? 'active' : ''}`}
          >
            <Share2 className="w-5 h-5" />
            Referrals
          </button>
          <button
            onClick={() => handleTabChange('invites')}
            className={`sidebar-link w-full ${activeTab === 'invites' ? 'active' : ''}`}
          >
            <Gift className="w-5 h-5" />
            Free Invites
          </button>
          <button
            onClick={() => handleTabChange('settings')}
            className={`sidebar-link w-full ${activeTab === 'settings' ? 'active' : ''}`}
          >
            <Settings className="w-5 h-5" />
            Settings
          </button>
        </nav>
        
        <div className="p-6 border-t border-white/5">
          <div className="mb-4">
            <p className="text-white font-medium truncate">{user?.name}</p>
            <p className="text-gold-400 text-sm">Admin</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8 overflow-auto pt-16 lg:pt-8">
        <div className="max-w-6xl">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <>
              <h1 className="text-3xl font-heading font-bold text-white mb-8">Admin Overview</h1>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="stat-card" data-testid="stat-users">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gold-500/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-gold-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Total Users</p>
                      <p className="text-2xl font-heading font-bold text-white">{stats?.total_users || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="stat-card" data-testid="stat-subscriptions">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/20 flex items-center justify-center">
                      <UserCheck className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Active Subscriptions</p>
                      <p className="text-2xl font-heading font-bold text-white">{stats?.active_subscriptions || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="stat-card" data-testid="stat-revenue">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Monthly Revenue</p>
                      <p className="text-2xl font-heading font-bold text-white">${stats?.monthly_revenue?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>

                <div className="stat-card" data-testid="stat-banner-clicks">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 flex items-center justify-center">
                      <MousePointerClick className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Kasidie Clicks</p>
                      <p className="text-2xl font-heading font-bold text-white" data-testid="banner-total-clicks">{bannerStats?.total_clicks ?? 0}</p>
                      <p className="text-xs text-zinc-600 mt-1" data-testid="banner-week-clicks">{bannerStats?.last_7_days ?? 0} in last 7 days</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Subscriptions */}
              <div className="glass-heavy p-6">
                <h2 className="text-xl font-heading font-semibold text-white mb-4">Recent Subscriptions</h2>
                {subscriptions.length > 0 ? (
                  <table className="table-royal">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Tier</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.slice(0, 5).map((sub) => {
                        const subUser = users.find(u => u.id === sub.user_id);
                        return (
                          <tr key={sub.id}>
                            <td className="text-white">{subUser?.email || 'Unknown'}</td>
                            <td className="text-zinc-300">{sub.tier_name}</td>
                            <td>{getStatusBadge(sub.status)}</td>
                            <td className="text-zinc-500">{new Date(sub.created_at).toLocaleDateString()}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-zinc-500 text-center py-8">No subscriptions yet</p>
                )}
              </div>
            </>
          )}

          {/* Users Tab */}
          {activeTab === 'users' && (
            <>
              <h1 className="text-3xl font-heading font-bold text-white mb-8">Users</h1>
              
              <div className="glass-heavy p-6">
                {users.length > 0 ? (
                  <table className="table-royal">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Membership</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => {
                        const userSub = subscriptions.find(s => s.user_id === u.id && s.status === 'ACTIVE');
                        return (
                          <tr key={u.id}>
                            <td className="text-white">{u.name}</td>
                            <td className="text-zinc-300">{u.email}</td>
                            <td>
                              <span className={`badge ${u.role === 'admin' ? 'badge-gold' : 'badge-gray'}`}>
                                {u.role}
                              </span>
                            </td>
                            <td>
                              {userSub ? (
                                <span className="text-green-400 text-sm">
                                  {userSub.tier_name} {userSub.is_free && '(Free)'}
                                </span>
                              ) : (
                                <span className="text-zinc-500 text-sm">None</span>
                              )}
                            </td>
                            <td className="text-zinc-500">{new Date(u.created_at).toLocaleDateString()}</td>
                            <td>
                              {u.role !== 'admin' && (
                                <button
                                  onClick={() => handleMakeAdmin(u.id)}
                                  className="text-gold-400 hover:text-gold-300 text-sm"
                                >
                                  Make Admin
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-zinc-500 text-center py-8">No users yet</p>
                )}
              </div>
            </>
          )}

          {/* Approvals Tab */}
          {activeTab === 'approvals' && (
            <>
              <h1 className="text-3xl font-heading font-bold text-white mb-8">Pending Approvals</h1>
              
              {pendingApprovals.length > 0 ? (
                <div className="glass-heavy p-6">
                  <p className="text-zinc-400 text-sm mb-6">
                    These members have paid but require your approval before getting access.
                  </p>
                  <table className="table-royal">
                    <thead>
                      <tr>
                        <th>Member</th>
                        <th>Email</th>
                        <th>Tier</th>
                        <th>Price</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingApprovals.map((sub) => (
                        <tr key={sub.id}>
                          <td className="text-white">{sub.user?.name || 'Unknown'}</td>
                          <td className="text-zinc-300">{sub.user?.email || 'Unknown'}</td>
                          <td className="text-gold-400">{sub.tier_name}</td>
                          <td className="text-zinc-300">${sub.tier_price}</td>
                          <td className="text-zinc-500">{new Date(sub.created_at).toLocaleDateString()}</td>
                          <td>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleApproveSubscription(sub.id)}
                                className="px-3 py-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 text-sm flex items-center gap-1"
                              >
                                <CheckCircle className="w-4 h-4" /> Approve
                              </button>
                              <button
                                onClick={() => handleRejectSubscription(sub.id)}
                                className="px-3 py-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 text-sm flex items-center gap-1"
                              >
                                <XCircle className="w-4 h-4" /> Reject
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="glass-heavy p-8 text-center">
                  <UserCheck className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <p className="text-white font-medium mb-2">No Pending Approvals</p>
                  <p className="text-zinc-500 text-sm">All subscription requests have been processed.</p>
                </div>
              )}
            </>
          )}

          {/* Subscriptions Tab */}
          {activeTab === 'subscriptions' && (
            <>
              <h1 className="text-3xl font-heading font-bold text-white mb-8">Subscriptions</h1>
              
              <div className="glass-heavy p-6">
                {subscriptions.length > 0 ? (
                  <table className="table-royal">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Tier</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Started</th>
                        <th>Expires</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub) => {
                        const subUser = users.find(u => u.id === sub.user_id);
                        return (
                          <tr key={sub.id}>
                            <td className="text-white">{subUser?.email || 'Unknown'}</td>
                            <td className="text-zinc-300">{sub.tier_name}</td>
                            <td>
                              {sub.is_free ? (
                                <span className="badge badge-gold"><Gift className="w-3 h-3 mr-1" /> Free</span>
                              ) : (
                                <span className="badge badge-gray">Paid</span>
                              )}
                            </td>
                            <td>{getStatusBadge(sub.status)}</td>
                            <td className="text-zinc-500">
                              {sub.started_at ? new Date(sub.started_at).toLocaleDateString() : '-'}
                            </td>
                            <td className="text-zinc-500">
                              {sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '-'}
                            </td>
                            <td>
                              {sub.status === 'ACTIVE' && (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleExtendMembership(sub.id)}
                                    className="text-green-400 hover:text-green-300 text-sm flex items-center gap-1"
                                    title="Extend"
                                  >
                                    <Clock className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleRevokeMembership(sub.id)}
                                    className="text-red-400 hover:text-red-300 text-sm flex items-center gap-1"
                                    title="Revoke"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-zinc-500 text-center py-8">No subscriptions yet</p>
                )}
              </div>
            </>
          )}

          {/* Tiers Tab */}
          {activeTab === 'tiers' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-heading font-bold text-white">Membership Tiers</h1>
                <div className="flex gap-4">
                  {tiers.length === 0 && (
                    <button
                      onClick={handleSeedTiers}
                      className="btn-outline text-sm py-2 px-4"
                      data-testid="seed-tiers-btn"
                    >
                      Seed Default Tiers
                    </button>
                  )}
                  <button
                    onClick={() => openTierDialog()}
                    className="btn-gold text-sm py-2 px-4 flex items-center gap-2"
                    data-testid="add-tier-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add Tier
                  </button>
                </div>
              </div>

              {/* PayPal Setup Guide */}
              {tiers.some(t => !t.paypal_plan_id) && (
                <div className="glass-panel p-4 mb-6 border-l-4 border-amber-500">
                  <h3 className="text-amber-400 font-medium mb-2">⚠️ PayPal Setup Required</h3>
                  <p className="text-zinc-400 text-sm mb-3">
                    Some tiers are missing PayPal Plan IDs. To accept payments:
                  </p>
                  <ol className="text-zinc-400 text-sm space-y-1 list-decimal list-inside mb-3">
                    <li>Go to <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noopener noreferrer" className="text-gold-400 underline">PayPal Developer Dashboard</a></li>
                    <li>Create a subscription plan for each tier (matching the price)</li>
                    <li>Copy the Plan ID (starts with P-)</li>
                    <li>Edit each tier below and paste the Plan ID</li>
                  </ol>
                  <p className="text-zinc-500 text-xs">
                    For live payments, switch to Production credentials in PayPal and update your environment variables.
                  </p>
                </div>
              )}

              {/* All tiers configured indicator */}
              {tiers.length > 0 && tiers.every(t => t.paypal_plan_id) && (
                <div className="glass-panel p-4 mb-6 border-l-4 border-green-500">
                  <h3 className="text-green-400 font-medium">✓ PayPal Configured</h3>
                  <p className="text-zinc-400 text-sm">All tiers have PayPal Plan IDs. Ready to accept payments!</p>
                </div>
              )}
              
              <div className="grid md:grid-cols-3 gap-6">
                {tiers.map((tier) => (
                  <div key={tier.id} className={`tier-card ${tier.is_popular ? 'popular' : ''}`}>
                    {tier.is_popular && (
                      <span className="badge badge-gold mb-4 inline-block">Popular</span>
                    )}
                    <h3 className="text-xl font-heading font-semibold text-white mb-2">{tier.name}</h3>
                    <p className="text-zinc-500 text-sm mb-4">{tier.description}</p>
                    <p className="text-3xl font-heading font-bold gold-text mb-4">${tier.price}</p>
                    
                    {/* PayPal Status */}
                    <div className="mb-4">
                      {tier.paypal_plan_id ? (
                        <span className="text-green-400 text-xs flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> PayPal: {tier.paypal_plan_id.slice(0, 12)}...
                        </span>
                      ) : (
                        <span className="text-amber-400 text-xs">⚠️ No PayPal Plan ID</span>
                      )}
                    </div>
                    
                    <ul className="space-y-2 mb-6">
                      {tier.features.map((f, i) => (
                        <li key={i} className="text-zinc-400 text-sm">• {f}</li>
                      ))}
                    </ul>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openTierDialog(tier)}
                        className="flex-1 btn-outline py-2 text-sm flex items-center justify-center gap-1"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteTier(tier.id)}
                        className="px-3 py-2 bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {tiers.length === 0 && (
                <div className="glass-heavy p-8 text-center">
                  <p className="text-zinc-500 mb-4">No membership tiers configured yet.</p>
                  <button
                    onClick={handleSeedTiers}
                    className="btn-gold"
                  >
                    Create Default Tiers
                  </button>
                </div>
              )}
            </>
          )}

          {/* Referrals Tab */}
          {activeTab === 'referrals' && (
            <>
              <h1 className="text-3xl font-heading font-bold text-white mb-8">Referral Program</h1>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="stat-card">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gold-500/20 flex items-center justify-center">
                      <Users className="w-6 h-6 text-gold-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Total Referred Users</p>
                      <p className="text-2xl font-heading font-bold text-white">{referralStats?.total_referred_users || 0}</p>
                    </div>
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-500/20 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Total Commissions Paid</p>
                      <p className="text-2xl font-heading font-bold text-white">${referralStats?.total_commissions_paid?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 flex items-center justify-center">
                      <Share2 className="w-6 h-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-zinc-500 text-sm">Commission Rate</p>
                      <p className="text-2xl font-heading font-bold text-white">10%</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="glass-heavy p-6">
                <h2 className="text-xl font-heading font-semibold text-white mb-4">Top Referrers</h2>
                {referralStats?.top_referrers?.length > 0 ? (
                  <table className="table-royal">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Referral Code</th>
                        <th>Total Earnings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referralStats.top_referrers.map((referrer) => (
                        <tr key={referrer.id}>
                          <td className="text-white">{referrer.name}</td>
                          <td className="text-zinc-300">{referrer.email}</td>
                          <td className="text-gold-400 font-mono">{referrer.referral_code}</td>
                          <td className="text-green-400 font-medium">${referrer.referral_earnings?.toFixed(2) || '0.00'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-zinc-500 text-center py-8">No referrals yet. Share your members&apos; referral codes to grow!</p>
                )}
              </div>
            </>
          )}
          {/* Free Invites Tab */}
          {activeTab === 'invites' && (
            <>
              <div className="flex items-center justify-between mb-8">
                <h1 className="text-3xl font-heading font-bold text-white">Free Membership Invites</h1>
                <button
                  onClick={() => setGrantDialogOpen(true)}
                  className="btn-gold text-sm py-2 px-4 flex items-center gap-2"
                  data-testid="create-invite-btn"
                >
                  <Plus className="w-4 h-4" />
                  Create Invite Link
                </button>
              </div>

              <p className="text-zinc-400 mb-6">
                Create special invite links to give free memberships to specific people. Share the link and they&apos;ll get instant access when they sign up.
              </p>
              
              <div className="glass-heavy p-6">
                {invites.length > 0 ? (
                  <table className="table-royal">
                    <thead>
                      <tr>
                        <th>Token</th>
                        <th>Tier</th>
                        <th>Duration</th>
                        <th>Uses</th>
                        <th>Status</th>
                        <th>Note</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invites.map((invite) => (
                        <tr key={invite.id}>
                          <td className="text-gold-400 font-mono">{invite.token}</td>
                          <td className="text-white">{invite.tier_name}</td>
                          <td className="text-zinc-300">{invite.duration_days} days</td>
                          <td className="text-zinc-300">{invite.uses} / {invite.max_uses}</td>
                          <td>
                            {invite.status === 'ACTIVE' && invite.uses < invite.max_uses ? (
                              <span className="badge badge-green">Active</span>
                            ) : invite.uses >= invite.max_uses ? (
                              <span className="badge badge-gray">Used</span>
                            ) : (
                              <span className="badge badge-red">Deactivated</span>
                            )}
                          </td>
                          <td className="text-zinc-500 text-sm">{invite.note || '-'}</td>
                          <td>
                            <div className="flex gap-2">
                              <button
                                onClick={() => copyInviteLink(invite.token)}
                                className="text-gold-400 hover:text-gold-300 text-sm flex items-center gap-1"
                                title="Copy Link"
                              >
                                {copiedToken === invite.token ? (
                                  <><CheckCircle className="w-4 h-4" /> Copied</>
                                ) : (
                                  <><Copy className="w-4 h-4" /> Copy</>
                                )}
                              </button>
                              {invite.status === 'ACTIVE' && (
                                <button
                                  onClick={() => handleDeactivateInvite(invite.token)}
                                  className="text-red-400 hover:text-red-300 text-sm"
                                  title="Deactivate"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-8">
                    <Gift className="w-12 h-12 text-gold-400 mx-auto mb-4" />
                    <p className="text-zinc-500 mb-4">No invite links created yet.</p>
                    <button
                      onClick={() => setGrantDialogOpen(true)}
                      className="btn-gold"
                    >
                      Create Your First Invite
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <>
              <h1 className="text-3xl font-heading font-bold text-white mb-8">Settings</h1>
              
              {/* PayPal Configuration */}
              <div className="glass-heavy p-6 mb-6">
                <h2 className="text-xl font-heading font-semibold text-white mb-6 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-gold-400" />
                  PayPal Configuration
                </h2>

                {/* Status Overview */}
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className={`p-4 border ${paypalStatus?.has_credentials ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
                    <p className="text-zinc-400 text-xs uppercase mb-1">API Credentials</p>
                    <p className={`font-medium ${paypalStatus?.has_credentials ? 'text-green-400' : 'text-red-400'}`}>
                      {paypalStatus?.has_credentials ? '✓ Configured' : '✗ Missing'}
                    </p>
                  </div>
                  <div className={`p-4 border ${paypalStatus?.connection_ok ? 'border-green-500/30 bg-green-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
                    <p className="text-zinc-400 text-xs uppercase mb-1">Connection</p>
                    <p className={`font-medium ${paypalStatus?.connection_ok ? 'text-green-400' : 'text-red-400'}`}>
                      {paypalStatus?.connection_ok ? '✓ Connected' : '✗ Failed'}
                    </p>
                  </div>
                  <div className="p-4 border border-amber-500/30 bg-amber-500/10">
                    <p className="text-zinc-400 text-xs uppercase mb-1">Mode</p>
                    <p className="text-amber-400 font-medium">
                      {paypalStatus?.is_sandbox ? '⚠ Sandbox' : '✓ Live'}
                    </p>
                  </div>
                  <div className={`p-4 border ${paypalStatus?.tiers_configured === paypalStatus?.tiers_total && paypalStatus?.tiers_total > 0 ? 'border-green-500/30 bg-green-500/10' : 'border-amber-500/30 bg-amber-500/10'}`}>
                    <p className="text-zinc-400 text-xs uppercase mb-1">Tiers with Plan IDs</p>
                    <p className={`font-medium ${paypalStatus?.tiers_configured === paypalStatus?.tiers_total && paypalStatus?.tiers_total > 0 ? 'text-green-400' : 'text-amber-400'}`}>
                      {paypalStatus?.tiers_configured || 0} / {paypalStatus?.tiers_total || 0}
                    </p>
                  </div>
                </div>

                {/* Ready Status */}
                {paypalStatus?.ready_for_payments ? (
                  <div className="p-4 border-l-4 border-green-500 bg-green-500/10 mb-6">
                    <p className="text-green-400 font-medium">✓ Ready to Accept Payments</p>
                    <p className="text-zinc-400 text-sm">Your PayPal integration is fully configured.</p>
                  </div>
                ) : (
                  <div className="p-4 border-l-4 border-amber-500 bg-amber-500/10 mb-6">
                    <p className="text-amber-400 font-medium flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> Setup Required
                    </p>
                    <p className="text-zinc-400 text-sm">Complete the steps below to accept live payments.</p>
                  </div>
                )}

                {/* Webhook URL */}
                <div className="mb-6">
                  <label className="block text-sm text-zinc-400 mb-2">Webhook URL (copy this to PayPal)</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin.replace('localhost:3000', window.location.host)}/api/webhooks/paypal`}
                      className="input-royal flex-1 font-mono text-sm"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin.replace('localhost:3000', window.location.host)}/api/webhooks/paypal`);
                        setCopiedWebhook(true);
                        setTimeout(() => setCopiedWebhook(false), 2000);
                      }}
                      className="btn-outline px-4"
                    >
                      {copiedWebhook ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Setup Instructions */}
                <div className="border border-white/10 p-4">
                  <h3 className="text-white font-medium mb-4">Go Live Checklist</h3>
                  
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${paypalStatus?.has_credentials ? 'bg-green-500' : 'bg-zinc-700'}`}>
                        {paypalStatus?.has_credentials ? <CheckCircle className="w-4 h-4 text-white" /> : <span className="text-white text-sm">1</span>}
                      </div>
                      <div>
                        <p className="text-white font-medium">Get Live API Credentials</p>
                        <p className="text-zinc-500 text-sm">Go to PayPal Developer Dashboard → Apps & Credentials → <strong>Live</strong> tab</p>
                        <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener noreferrer" className="text-gold-400 text-sm inline-flex items-center gap-1 mt-1 hover:underline">
                          Open PayPal Dashboard <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${paypalStatus?.tiers_configured === paypalStatus?.tiers_total && paypalStatus?.tiers_total > 0 ? 'bg-green-500' : 'bg-zinc-700'}`}>
                        {paypalStatus?.tiers_configured === paypalStatus?.tiers_total && paypalStatus?.tiers_total > 0 ? <CheckCircle className="w-4 h-4 text-white" /> : <span className="text-white text-sm">2</span>}
                      </div>
                      <div>
                        <p className="text-white font-medium">Create Live Subscription Plans</p>
                        <p className="text-zinc-500 text-sm">Create a subscription plan for each tier in PayPal (in Live mode)</p>
                        <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener noreferrer" className="text-gold-400 text-sm inline-flex items-center gap-1 mt-1 hover:underline">
                          PayPal Subscriptions <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-zinc-700">
                        <span className="text-white text-sm">3</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">Update Environment Variables</p>
                        <p className="text-zinc-500 text-sm">Update these in your deployment settings:</p>
                        <code className="block mt-2 p-2 bg-black/50 text-green-400 text-xs font-mono">
                          PAYPAL_CLIENT_ID=your-live-client-id<br/>
                          PAYPAL_CLIENT_SECRET=your-live-secret<br/>
                          REACT_APP_PAYPAL_CLIENT_ID=your-live-client-id
                        </code>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-zinc-700">
                        <span className="text-white text-sm">4</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">Configure Webhook</p>
                        <p className="text-zinc-500 text-sm">Add a webhook in PayPal with the URL above. Subscribe to:</p>
                        <ul className="text-zinc-500 text-sm mt-1 list-disc list-inside">
                          <li>BILLING.SUBSCRIPTION.ACTIVATED</li>
                          <li>BILLING.SUBSCRIPTION.CANCELLED</li>
                          <li>BILLING.SUBSCRIPTION.EXPIRED</li>
                          <li>PAYMENT.SALE.COMPLETED</li>
                        </ul>
                      </div>
                    </div>

                    <div className="flex gap-3">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-zinc-700">
                        <span className="text-white text-sm">5</span>
                      </div>
                      <div>
                        <p className="text-white font-medium">Update Tier Plan IDs</p>
                        <p className="text-zinc-500 text-sm">Go to Tiers tab and update each tier with its Live Plan ID</p>
                        <button onClick={() => setActiveTab('tiers')} className="text-gold-400 text-sm mt-1 hover:underline">
                          Go to Tiers →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Telegram Configuration */}
              <div className="glass-heavy p-6">
                <h2 className="text-xl font-heading font-semibold text-white mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-gold-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.692-1.653-1.123-2.678-1.799-1.185-.781-.417-1.21.258-1.911.177-.184 3.247-2.977 3.307-3.23.007-.032.015-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.009-1.252-.242-1.865-.442-.751-.244-1.349-.374-1.297-.789.027-.216.324-.437.893-.663 3.498-1.524 5.831-2.529 6.998-3.015 3.333-1.386 4.025-1.627 4.477-1.635.099-.002.321.023.465.141.12.098.153.228.166.331.014.103.03.336.018.519z"/></svg>
                  Telegram Bot
                </h2>
                <p className="text-zinc-400 text-sm mb-4">Your Telegram bot is configured to generate invite links for members.</p>
                <div className="p-3 bg-black/30 border border-white/10">
                  <p className="text-zinc-500 text-xs">Make sure your bot is added as an <strong className="text-white">admin</strong> to your Telegram group with permission to create invite links.</p>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Tier Dialog */}
      <Dialog open={tierDialogOpen} onOpenChange={setTierDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              {editingTier ? 'Edit Tier' : 'Create Tier'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Name</label>
              <input
                type="text"
                value={tierForm.name}
                onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                className="input-royal w-full"
                placeholder="e.g., Royal"
              />
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Price (USD/month)</label>
              <input
                type="number"
                value={tierForm.price}
                onChange={(e) => setTierForm({ ...tierForm, price: e.target.value })}
                className="input-royal w-full"
                placeholder="29.99"
                step="0.01"
              />
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Description</label>
              <input
                type="text"
                value={tierForm.description}
                onChange={(e) => setTierForm({ ...tierForm, description: e.target.value })}
                className="input-royal w-full"
                placeholder="Short description"
              />
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Features (one per line)</label>
              <textarea
                value={tierForm.features}
                onChange={(e) => setTierForm({ ...tierForm, features: e.target.value })}
                className="input-royal w-full h-32 resize-none"
                placeholder="Feature 1&#10;Feature 2&#10;Feature 3"
              />
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">
                PayPal Plan ID <span className="text-gold-400">*Required for payments</span>
              </label>
              <input
                type="text"
                value={tierForm.paypal_plan_id}
                onChange={(e) => setTierForm({ ...tierForm, paypal_plan_id: e.target.value })}
                className="input-royal w-full font-mono"
                placeholder="P-XXXXXXXXXXXXXXXXXXXXXXXX"
              />
              <p className="text-zinc-600 text-xs mt-1">
                Get this from PayPal Developer Dashboard → Subscriptions → Plans
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_popular"
                checked={tierForm.is_popular}
                onChange={(e) => setTierForm({ ...tierForm, is_popular: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="is_popular" className="text-zinc-400 text-sm">Mark as popular</label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requires_approval"
                checked={tierForm.requires_approval}
                onChange={(e) => setTierForm({ ...tierForm, requires_approval: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="requires_approval" className="text-zinc-400 text-sm">Require admin approval</label>
            </div>
            
            <div className="flex gap-4 pt-4">
              <button
                onClick={() => setTierDialogOpen(false)}
                className="flex-1 btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTier}
                className="flex-1 btn-gold"
              >
                {editingTier ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Invite Dialog */}
      <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              Create Free Membership Invite
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Membership Tier</label>
              <select
                value={inviteForm.tier_id}
                onChange={(e) => setInviteForm({ ...inviteForm, tier_id: e.target.value })}
                className="input-royal w-full"
              >
                <option value="">Select a tier...</option>
                {tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name} (${tier.price}/mo value)
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Duration (days)</label>
              <input
                type="number"
                value={inviteForm.duration_days}
                onChange={(e) => setInviteForm({ ...inviteForm, duration_days: parseInt(e.target.value) })}
                className="input-royal w-full"
                placeholder="30"
                min="1"
              />
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Max Uses</label>
              <input
                type="number"
                value={inviteForm.max_uses}
                onChange={(e) => setInviteForm({ ...inviteForm, max_uses: parseInt(e.target.value) })}
                className="input-royal w-full"
                placeholder="1"
                min="1"
              />
              <p className="text-zinc-600 text-xs mt-1">How many people can use this invite link</p>
            </div>
            
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Note (optional)</label>
              <input
                type="text"
                value={inviteForm.note}
                onChange={(e) => setInviteForm({ ...inviteForm, note: e.target.value })}
                className="input-royal w-full"
                placeholder="e.g., For John - contest winner"
              />
            </div>
            
            <div className="flex gap-4 pt-4">
              <button
                onClick={() => setGrantDialogOpen(false)}
                className="flex-1 btn-outline"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateInvite}
                className="flex-1 btn-gold"
              >
                Create Invite Link
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
